import { useEffect, useState } from "react";
import { X, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export const assetUrl = (a) =>
  a.url?.startsWith("http") ? a.url : `${process.env.REACT_APP_BACKEND_URL}${a.url}`;

export const useAssets = () => {
  const [assets, setAssets] = useState([]);
  const load = () => api.get("/admin/assets").then((r) => setAssets(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);
  return { assets, reload: load };
};

export const uploadAsset = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const r = await api.post("/admin/assets", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return r.data;
};

export const AssetPicker = ({ open, onClose, onSelect }) => {
  const { assets, reload } = useAssets();
  const [uploading, setUploading] = useState(false);

  if (!open) return null;

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const asset = await uploadAsset(file);
      toast.success("Uploaded");
      reload();
      onSelect(assetUrl(asset));
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-ink/60 p-4 backdrop-blur-sm" onClick={onClose} data-testid="asset-picker-modal">
      <div className="max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-sand p-6 shadow-deep" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-xl font-semibold text-ink">Asset library</h3>
          <div className="flex items-center gap-2">
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-terra px-4 py-2 font-outfit text-xs font-semibold text-white hover:bg-terradark">
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              Upload
              <input data-testid="asset-picker-upload" type="file" accept="image/*" className="hidden" onChange={onUpload} />
            </label>
            <button data-testid="asset-picker-close" onClick={onClose} aria-label="Close">
              <X className="h-5 w-5 text-faint" />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
          {assets.map((a) => (
            <button
              key={a.id}
              data-testid={`asset-picker-item-${a.id}`}
              onClick={() => {
                onSelect(assetUrl(a));
                onClose();
              }}
              className="group overflow-hidden rounded-xl border border-ink/10 bg-white transition-all hover:border-terra"
            >
              <img src={assetUrl(a)} alt={a.original_filename} className="aspect-square w-full object-cover" loading="lazy" />
              <span className="block truncate px-2 py-1.5 text-[10px] text-faint">{a.original_filename}</span>
            </button>
          ))}
          {assets.length === 0 && <p className="col-span-full py-10 text-center text-sm text-faint">No assets yet — upload the first image.</p>}
        </div>
      </div>
    </div>
  );
};
